const SETTINGS_KEYS = new Set(['te_admin_pass', 'te_seeded_v3', 'te_hero_images', 'te_business']);
let runtimeMode = 'unknown';

function resourceForKey(key) {
  switch (key) {
    case 'te_menu':
      return 'menu';
    case 'te_orders':
      return 'orders';
    case 'te_reviews':
      return 'reviews';
    default:
      return null;
  }
}

async function apiFetch(path, options = {}) {
  const response = await fetch(path, {
    credentials: 'omit',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
    ...options,
  });

  const text = await response.text();
  const data = text ? JSON.parse(text) : null;
  if (!response.ok) {
    throw new Error(data?.message || `Request failed (${response.status})`);
  }
  return data;
}

function localGetParsed(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (raw == null) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function localSetParsed(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    return false;
  }
}

async function apiAvailable() {
  try {
    await apiFetch('/api/health');
    runtimeMode = 'api';
    return true;
  } catch {
    if (runtimeMode === 'unknown') runtimeMode = 'local';
    return false;
  }
}

const StorageService = {
  async getAll(key) {
    const resource = resourceForKey(key);
    if (!resource) return [];
    try {
      const data = await apiFetch(`/api/${resource}`);
      runtimeMode = 'api';
      return Array.isArray(data) ? data : [];
    } catch (err) {
      console.error(`StorageService.getAll failed for ${key}:`, err);
      runtimeMode = 'local';
      const local = localGetParsed(key, []);
      return Array.isArray(local) ? local : [];
    }
  },

  async get(key) {
    if (!SETTINGS_KEYS.has(key)) {
      return localGetParsed(key, null);
    }
    try {
      const value = await apiFetch(`/api/settings/${key}`);
      runtimeMode = 'api';
      return value;
    } catch (err) {
      console.error(`StorageService.get failed for ${key}:`, err);
      runtimeMode = 'local';
      return localGetParsed(key, null);
    }
  },

  async set(key, value) {
    try {
      if (SETTINGS_KEYS.has(key)) {
        await apiFetch(`/api/settings/${key}`, {
          method: 'PUT',
          body: JSON.stringify({ value }),
        });
        runtimeMode = 'api';
        return true;
      }

      const resource = resourceForKey(key);
      if (!resource) return false;
      await apiFetch(`/api/${resource}`, {
        method: 'PUT',
        body: JSON.stringify(value),
      });
      runtimeMode = 'api';
      return true;
    } catch (err) {
      console.error(`StorageService.set failed for ${key}:`, err);
      runtimeMode = 'local';
      return localSetParsed(key, value);
    }
  },

  async push(key, item) {
    const resource = resourceForKey(key);
    if (!resource) {
      const arr = localGetParsed(key, []);
      arr.push(item);
      localSetParsed(key, arr);
      return item;
    }
    try {
      const created = await apiFetch(`/api/${resource}`, {
        method: 'POST',
        body: JSON.stringify(item),
      });
      runtimeMode = 'api';
      return created;
    } catch (err) {
      console.error(`StorageService.push failed for ${key}:`, err);
      runtimeMode = 'local';
      const arr = localGetParsed(key, []);
      arr.push(item);
      localSetParsed(key, arr);
      return item;
    }
  },

  async update(key, id, patch) {
    const resource = resourceForKey(key);
    if (!resource) {
      const arr = localGetParsed(key, []);
      const idx = arr.findIndex((x) => x.id === id);
      if (idx === -1) return null;
      arr[idx] = { ...arr[idx], ...patch, updatedAt: new Date().toISOString() };
      localSetParsed(key, arr);
      return arr[idx];
    }
    try {
      const updated = await apiFetch(`/api/${resource}/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
      runtimeMode = 'api';
      return updated;
    } catch (err) {
      console.error(`StorageService.update failed for ${key}/${id}:`, err);
      runtimeMode = 'local';
      const arr = localGetParsed(key, []);
      const idx = arr.findIndex((x) => x.id === id);
      if (idx === -1) return null;
      arr[idx] = { ...arr[idx], ...patch, updatedAt: new Date().toISOString() };
      localSetParsed(key, arr);
      return arr[idx];
    }
  },

  async delete(key, id) {
    const resource = resourceForKey(key);
    if (!resource) {
      const arr = localGetParsed(key, []);
      localSetParsed(key, arr.filter((x) => x.id !== id));
      return true;
    }
    try {
      await apiFetch(`/api/${resource}/${id}`, { method: 'DELETE' });
      runtimeMode = 'api';
      return true;
    } catch (err) {
      console.error(`StorageService.delete failed for ${key}/${id}:`, err);
      runtimeMode = 'local';
      const arr = localGetParsed(key, []);
      localSetParsed(key, arr.filter((x) => x.id !== id));
      return true;
    }
  },

  async findById(key, id) {
    const resource = resourceForKey(key);
    if (!resource) {
      const arr = localGetParsed(key, []);
      return arr.find((x) => x.id === id) || null;
    }
    try {
      const found = await apiFetch(`/api/${resource}/${id}`);
      runtimeMode = 'api';
      return found;
    } catch (err) {
      console.error(`StorageService.findById failed for ${key}/${id}:`, err);
      runtimeMode = 'local';
      const arr = localGetParsed(key, []);
      return arr.find((x) => x.id === id) || null;
    }
  },

  async bootstrap({ menu, reviews, orders = [], adminPassword }) {
    if (await apiAvailable()) {
      try {
        return await apiFetch('/api/bootstrap', {
          method: 'POST',
          body: JSON.stringify({
            menu,
            reviews,
            orders,
            adminPassword,
          }),
        });
      } catch (err) {
        console.error('StorageService.bootstrap API failed, using local fallback:', err);
        runtimeMode = 'local';
      }
    }

    runtimeMode = 'local';
    localSetParsed('te_menu', menu);
    localSetParsed('te_reviews', reviews);
    localSetParsed('te_orders', orders);
    localSetParsed('te_admin_pass', adminPassword);
    localSetParsed('te_seeded_v3', true);
    return { seeded: true, mode: 'local' };
  },

  getRuntimeMode() {
    return runtimeMode;
  },
};

export default StorageService;
